import {
  BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";

const COLORS = ["var(--accent)", "#22c55e", "#f59e0b", "#a855f7", "#ef4444", "#06b6d4"];

const DATE_PATTERNS = /date|time|tarih|yil|year|ay|month|gun|day|period|donem|hafta|week/i;
const NUM_PATTERNS = /int|float|decimal|numeric|money|number|tutar|miktar|adet|fiyat|toplam|amount|total|count|sum|avg|ort/i;

function detectColTypes(columns, rows) {
  const dateGroups = [];
  const numericCols = [];
  let labelCol = null;

  columns.forEach((col) => {
    const sample = rows.slice(0, 20).map((r) => r[col]).find((v) => v !== null && v !== undefined);
    const isDateByName = DATE_PATTERNS.test(col);
    const isNumByName = NUM_PATTERNS.test(col);
    const isDateByValue = sample && (
      (typeof sample === "string" && /^\d{4}-\d{2}/.test(sample)) ||
      (typeof sample === "string" && /^\d{2}[./]\d{2}[./]\d{4}/.test(sample))
    );
    const isNumByValue = sample !== null && sample !== undefined && typeof sample === "number";

    if (isDateByName || isDateByValue) {
      dateGroups.push(col);
    } else if (isNumByName || isNumByValue) {
      numericCols.push(col);
    } else if (!labelCol) {
      labelCol = col;
    }
  });

  return { dateGroups, numericCols, labelCol };
}

function buildChartData(rows, xCol, numericCols, maxPoints = 60) {
  const step = rows.length > maxPoints ? Math.ceil(rows.length / maxPoints) : 1;
  return rows
    .filter((_, i) => i % step === 0)
    .map((row) => {
      const entry = { _x: row[xCol] !== null && row[xCol] !== undefined ? String(row[xCol]) : "" };
      numericCols.forEach((col) => {
        entry[col] = row[col] !== null && row[col] !== undefined ? Number(row[col]) : null;
      });
      return entry;
    });
}

function shorten(label, max = 18) {
  if (!label) return "";
  const s = String(label);
  return s.length > max ? s.slice(0, max) + "…" : s;
}

function ChartCard({ title, children }) {
  return (
    <div style={{ background: "var(--bg-card)", borderRadius: 10, padding: "18px 20px", marginBottom: 18 }}>
      <div style={{ color: "var(--text-secondary)", fontSize: 13, fontWeight: 600, marginBottom: 14 }}>{title}</div>
      {children}
    </div>
  );
}

const tooltipStyle = {
  contentStyle: { background: "var(--bg-page)", border: "1px solid var(--border)", borderRadius: 8, fontSize: 12 },
  labelStyle: { color: "var(--text-primary)" },
  itemStyle: { color: "var(--text-secondary)" },
};

export default function QueryCharts({ columns, rows }) {
  if (!columns?.length || !rows?.length) return null;

  const { dateGroups, numericCols, labelCol } = detectColTypes(columns, rows);

  const charts = [];

  // Line chart: her tarih kolonu x ekseni olarak
  dateGroups.forEach((dateCol, i) => {
    if (!numericCols.length) return;
    const data = buildChartData(rows, dateCol, numericCols);
    charts.push(
      <ChartCard key={`line-${i}`} title={`Zaman Serisi — ${dateCol}`}>
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={data} margin={{ top: 0, right: 20, bottom: 36, left: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
            <XAxis
              dataKey="_x"
              tick={{ fill: "var(--text-muted)", fontSize: 11 }}
              angle={-30}
              textAnchor="end"
              tickFormatter={(v) => shorten(v, 12)}
            />
            <YAxis tick={{ fill: "var(--text-muted)", fontSize: 11 }} />
            <Tooltip {...tooltipStyle} />
            <Legend wrapperStyle={{ color: "var(--text-secondary)", fontSize: 12 }} />
            {numericCols.slice(0, 5).map((col, ci) => (
              <Line
                key={col}
                type="monotone"
                dataKey={col}
                stroke={COLORS[ci % COLORS.length]}
                dot={data.length <= 20}
                strokeWidth={2}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </ChartCard>
    );
  });

  // Bar chart: label kolonu veya ilk metin kolonu x ekseni
  const barXCol = labelCol || columns.find((c) => !numericCols.includes(c) && !dateGroups.includes(c)) || columns[0];
  if (numericCols.length > 0 && barXCol) {
    const data = buildChartData(rows, barXCol, numericCols, 40);
    charts.push(
      <ChartCard key="bar" title={`Karşılaştırma — ${barXCol}`}>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={data} margin={{ top: 0, right: 20, bottom: 40, left: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
            <XAxis
              dataKey="_x"
              tick={{ fill: "var(--text-muted)", fontSize: 11 }}
              angle={-30}
              textAnchor="end"
              tickFormatter={(v) => shorten(v, 14)}
            />
            <YAxis tick={{ fill: "var(--text-muted)", fontSize: 11 }} />
            <Tooltip {...tooltipStyle} />
            <Legend wrapperStyle={{ color: "var(--text-secondary)", fontSize: 12 }} />
            {numericCols.slice(0, 5).map((col, ci) => (
              <Bar key={col} dataKey={col} fill={COLORS[ci % COLORS.length]} radius={[4, 4, 0, 0]} maxBarSize={40} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>
    );
  }

  if (charts.length === 0) {
    return (
      <div style={{ color: "var(--text-faint)", fontSize: 13, padding: "12px 0" }}>
        Grafik oluşturmak için sayısal veya tarih kolonu bulunamadı.
      </div>
    );
  }

  return <div style={{ marginTop: 8 }}>{charts}</div>;
}
